import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import AddressLink from '@repo/ui/address-link'
import DataTable from '@repo/ui/data-table'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCenterOlJourneyPlace } from '../../../../../api/centers/get-center-ol-journey-place'
import { useNativeCountryName } from '../../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { CenterProperties } from '../../../../../lib/types/models/center/properties.center'
import { OlJourneyPlace } from '../../../../../lib/types/models/ol/ol-journey-place'

type Props = {
  centerIds?: number[]
}

const StaffPersonalSpaceCenterOlJourneyPlaceSection = ({ centerIds }: Props) => {
  const { t } = useTranslate()
  const nativeCountryName = useNativeCountryName()

  const { data, isPending } = useQuery({
    initialData: [],
    queryKey: ['center-ol-journey-place', centerIds],
    queryFn: async () => {
      if (!centerIds) throw new Error('No center ID found')

      const olJourneyPlaces = await Promise.all(
        centerIds.map((centerId) => getCenterOlJourneyPlace(centerId))
      )

      return olJourneyPlaces.filter((place) => !!place)
    },
    enabled: !!centerIds
  })

  if (isPending) return <Loader center />

  if (!data)
    return (
      <Typography variant="text" color={tailwindColors.grey.active}>
        {t('staff.personal.space.my.journeys.details.tab.home.teaching.halls.not.found')}
      </Typography>
    )

  return (
    <InnerBox className="rounded-lg p-3 bg-white-hover">
      <div className="flex items-center gap-2">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          {t('staff.personal.space.my.journeys.details.tab.home.teaching.halls')}
        </Typography>
      </div>
      <div className="h-2" />
      <DataTable<OlJourneyPlace>
        paginated={false}
        bodyVariant="white"
        columns={[
          {
            accessorKey: 'name',
            header: t('staff.personal.space.my.journeys.details.tab.home.teaching.halls.name'),
            cell: ({ row }: { row: { original: OlJourneyPlace } }) => {
              const addressInfo = CenterProperties.getCenterTeachingCompleteAddress(
                row.original,
                nativeCountryName
              )
              if (!addressInfo?.address) return <span>-</span>
              return (
                <AddressLink
                  className="text-wrap"
                  name={addressInfo?.name}
                  address1={addressInfo?.address}
                />
              )
            }
          },
          {
            accessorKey: 'tuitionAddress1',
            header: t('staff.personal.space.my.journeys.details.tab.home.teaching.halls.address'),
            cell: ({ row }) => (
              <Typography
                variant="secondary-body"
                className="mobile:text-wrap mobile:py-2 break-words text-balance"
                color={tailwindColors.grey.DEFAULT}
              >
                {valueRender(
                  CenterProperties.getCenterTeachingCompleteAddress(
                    row.original,
                    nativeCountryName
                  )?.address
                )}
              </Typography>
            )
          }
        ]}
        data={data}
        rowCount={data.length}
      />
    </InnerBox>
  )
}

export default StaffPersonalSpaceCenterOlJourneyPlaceSection
